import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDeleteCartItems, useEstimateOrder } from '@/apis/rest/generated/hooks'
import {
  ErrorResponseDto,
  EstimateCartResponseDto,
  FitpetErrorSubCodeEnum,
  OrderItemForCartScheme,
  ShippingForCartScheme,
} from '@/apis/rest/generated/types'
import ROUTES from '@/constants/legacy/constRoutes'
import useCartEventTracker from '@/containers/event-tracker/cart.event-tracker'
import { useCustomRouter, useUserInfo } from '@/containers/hooks'
import { useAfterLogin } from '@/containers/hooks/auth'
import { useGetCartData } from '@/containers/hooks/cart/useGetCartData'
import { hasSignedIn } from '@/containers/hooks/useUserInfo'
import { UserContainer } from '@/containers/users/UserContainer'
import { useAdCouponGroupsQuery } from '@/graphql/generated/hooks'
import { ErrorType } from '@/plugins/rest-api-v4'
import { doSetBottomSheetData, doSetEstimateOrderRequestData } from '@/stores/reduxData'
import { RootState } from '@/stores/store'
import { checkAvailableCartItem } from '@/utils/checkAvailableCartItem'
import { isCouponError } from '@/utils/isCouponError'
import { localCartShippingGroups, localEstimateTarget } from '@/utils/utilLocalStorage'
import { tenThousandCurrencyText } from '@/utils/utilNumber'

interface OrderItemWithChecked extends OrderItemForCartScheme {
  isChecked: boolean
  appliedCouponId?: number
}

export interface ShippingForCart extends ShippingForCartScheme {
  orderItems: OrderItemWithChecked[]
}

export interface CartResponseDtoWithChecked extends EstimateCartResponseDto {
  shippings: ShippingForCart[]
}

export const useCartContents = () => {
  const [isCartError, setIsCartError] = useState(false)
  const [welcomeDealError, setWelcomeDealError] = useState<{ title: string; contents?: string } | null>(null)

  const dispatch = useDispatch()
  const cartStorageData = useSelector((state: RootState) => state.reduxDataReducers.cartData)
  const { loginOperator } = useAfterLogin()
  const { trackClickCartPaymentButtonV2Event } = useCartEventTracker()
  const { useAuthenticatedRoute } = useUserInfo
  const { routeToAddInfo } = useAuthenticatedRoute()
  const { me, meIsLoading } = UserContainer.useContainer()
  const { push } = useCustomRouter()
  const { initCartData, getCartDataByChecked, cartDataParams } = useGetCartData()

  const { data: appCouponData } = useAdCouponGroupsQuery({
    skip: !me,
    variables: {
      slug: 'app-only',
    },
  })

  const { mutate: deleteCartItem } = useDeleteCartItems()
  const { mutate: estimateOrder } = useEstimateOrder()

  useEffect(() => {
    if (!me) return

    initCartData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meIsLoading])

  // 전체 체크
  const allCheckProduct = (isCheck: boolean) => {
    if (!cartStorageData) return

    const updatedCartData = {
      ...cartStorageData,
      shippings: cartStorageData.shippings.map((group) => ({
        ...group,
        orderItems: group.orderItems.map((item) => ({
          ...item,
          isChecked: checkAvailableCartItem(item) ? isCheck : false,
        })),
      })),
    }

    const param = isCheck
      ? updatedCartData.shippings
          .flatMap((group) => group.orderItems)
          .filter(checkAvailableCartItem)
          .map((item) => ({
            productCouponId: item.appliedCouponId,
            checkedCartItemId: item.cartItemId,
          }))
      : []

    getCartDataByChecked({ param })
  }

  // 선택된 상품 삭제 처리
  const deleteSelected = () => {
    const checkedCartItemIds =
      cartStorageData?.shippings
        .flatMap((group) => group.orderItems)
        .filter((item) => item.isChecked)
        .map((item) => ({ id: item.cartItemId })) || []

    deleteCartItem(
      { data: { cartItems: checkedCartItemIds } },
      { onSuccess: () => getCartDataByChecked({ param: [] }) }
    )
  }

  // 결제하기 클릭
  const confirmOrder = async () => {
    if (!hasSignedIn()) {
      loginOperator(ROUTES.CART, false)
      return
    }

    if (!me?.mobileNumber) {
      routeToAddInfo()
      return
    }

    const orderItems =
      cartStorageData?.shippings
        .map((group) => ({
          ...group,
          orderItems: group.orderItems.filter((item) => item.isChecked),
        }))
        .filter((group) => group.orderItems.length > 0)
        .map((group) => ({
          products: group.orderItems.map((item) => ({
            productId: item.productId,
            productOptionId: item.productOptionId,
            productCouponId: item.appliedCouponId,
            productPromotionId: item.productPromotionId,
            quantity: item.quantity,
          })),
        })) || []

    const estimateData = { mileage: undefined, shippings: orderItems }
    localEstimateTarget.save(estimateData)
    dispatch(doSetEstimateOrderRequestData(estimateData))

    localCartShippingGroups.remove()

    estimateOrder(
      { data: estimateData },
      {
        onSuccess: () => {
          const purchaseTarget =
            cartStorageData?.shippings.flatMap((group) => group.orderItems).filter((item) => item.isChecked) || []

          trackClickCartPaymentButtonV2Event({
            purchaseTarget,
            totalProductAmount: cartStorageData?.productAmount,
            totalShipmentAmount: cartStorageData?.shippingAmount,
          })
          push(ROUTES.ORDER.BASE)
        },
        onError: handleOrderError,
      }
    )
  }

  // 결제하기 버튼 클릭 후 Order 호출에서 발생하는 에러 핸들러
  const handleOrderError = (err: ErrorType<ErrorResponseDto>) => {
    const errorCode = err.response?.data?.errors[0].subCode
    const parameterValue = err.response?.data?.errors[0]?.parameterValue

    if (errorCode && isCouponError(errorCode)) {
      getCartDataByChecked({
        param: cartDataParams,
        onError: (err) => err && setIsCartError(true),
      })
    } else if (errorCode === FitpetErrorSubCodeEnum.PROMOTION_MIN_ORDER_AMOUNT_NOT_SATISFIED && parameterValue) {
      setWelcomeDealError({
        title: `이벤트 상품 외\n${tenThousandCurrencyText(Number(parameterValue))} 이상 주문 시 구매 가능합니다.`,
      })
    } else if (errorCode === FitpetErrorSubCodeEnum.SPECIAL_DEAL_ORDER_QUANTITY_IS_ONE) {
      setWelcomeDealError({
        title: '웰컴딜 상품은\n1개만 구매 가능합니다.',
        contents: parameterValue,
      })
    } else if (errorCode === FitpetErrorSubCodeEnum.ALREADY_BOUGHT_SPECIAL_DEAL) {
      setWelcomeDealError({
        title: '웰컴딜 상품은\n첫 구매 시에만 구매 가능합니다',
        contents: parameterValue,
      })
    } else {
      setIsCartError(true)
    }
  }

  // 장바구니 에러 모달 confirm 버튼 클릭시
  const handleConfirmCartError = () => {
    setIsCartError(false)
    getCartDataByChecked({
      param: cartDataParams,
      onSuccess: (res) => {
        if (res) {
          const availableCartItems = res.shippings
            .flatMap((shipping) => shipping.orderItems)
            .filter((item) => item.isChecked && checkAvailableCartItem(item))
            .map((item) => ({
              productCouponId: item.appliedCouponId,
              checkedCartItemId: item.cartItemId,
            }))
          getCartDataByChecked({ param: availableCartItems })
        }
      },
    })
  }

  // 웰컴딜 에러 모달 confirm 버튼 클릭시
  const handleConfirmWelcomeDealError = () => {
    setWelcomeDealError(null)
  }

  // 쿠폰 적용 에러 발생시 estimateCart param 에서 해당 쿠폰 제거
  const handleConfirmCartCouponError = (targetIds: number[]) => {
    const updatedParams = cartDataParams.map((item) =>
      item.productCouponId && targetIds.includes(item.productCouponId) ? { ...item, productCouponId: undefined } : item
    )

    getCartDataByChecked({
      param: updatedParams,
      onSuccess: () => dispatch(doSetBottomSheetData({ isCartProductCouponVisible: false })),
    })
  }

  // 적용돤 쿠폰 에러 발생시 estimateCart param 에서 해당 쿠폰 제거
  const handleRemoveParamsCouponByCouponIds = (couponIds: number[]) => {
    const updatedParams = cartDataParams.map((item) =>
      item.productCouponId && couponIds.includes(item.productCouponId) ? { ...item, productCouponId: undefined } : item
    )
    getCartDataByChecked({ param: updatedParams })
  }

  const availableCartItems =
    cartStorageData?.shippings?.flatMap((shipping) => shipping.orderItems).filter(checkAvailableCartItem) || []

  const isAllChecked = availableCartItems.every((item) => item.isChecked)
  const allCountItemCount = availableCartItems.filter((item) => item.isChecked).length

  return {
    allCheckProduct,
    isAllChecked,
    deleteSelected,
    confirmOrder,
    handleConfirmCartError,
    isCartError,
    handleRemoveParamsCouponByCouponIds,
    allCountItemCount,
    welcomeDealError,
    handleConfirmWelcomeDealError,
    handleConfirmCartCouponError,
    appCouponData,
  }
}
